import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import pagedown from 'pagedown';
import MultipleChoiceAnswer from './MultipleChoiceAnswer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
} from '@whitebox-learning/fontawesome-pro-light-svg-icons';

const converter = pagedown.getSanitizingConverter();

class MultipleChoiceQuestion extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showFeedback: false,
      selectedAnswer: -1,
    };

    this.tryAgainButtonRef = React.createRef();
    this.questionRef = React.createRef();
    this.firstAnswerRef = React.createRef();
  }

  tryAgain = () => {
    this.setState({
      showFeedback: false,
      selectedAnswer: -1,
    });
    // Focus first answer choice after reset
    setTimeout(() => {
      if (this.firstAnswerRef.current) {
        this.firstAnswerRef.current.focus();
      }
    }, 0);
  };

  componentDidUpdate(prevProps) {
    if (this.props.response.attempts !== prevProps.response.attempts) {
      this.setState({
        showFeedback: true,
      });
      // Focus management after answer submission
      setTimeout(() => {
        if (!this.props.response.isCorrect && this.tryAgainButtonRef.current) {
          // If incorrect, focus "Try Again" button
          this.tryAgainButtonRef.current.focus();
        } else if (this.props.response.isCorrect && this.props.onAnswerCorrect) {
          // If correct, notify parent to focus "Next Question" button
          this.props.onAnswerCorrect();
        }
      }, 100);
    }
  }

  renderFeedback() {
    if (this.props.checking === true) {
      return (
        <div className="answer-response-container">
          <h4>Checking...</h4>
        </div>
      );
    }

    let desc;

    if (this.props.response.isAnswered) {
      desc = this.renderCorrectAnswerHTML();
    } else if (!this.props.response.isCorrect) {
      desc = (
        <div>
          <p className="lead">
            <FontAwesomeIcon icon={faTimes} /> Not quite.
          </p>
          <Button
            variant="primary"
            onClick={this.tryAgain}
            ref={this.tryAgainButtonRef}
          >
            Try Again!
          </Button>
        </div>
      );
    }

    return (
      <div
        className={
          'answer-response-container ' +
          (this.props.response.isCorrect ? 'correct' : 'incorrect')
        }
      >
        <div className="box box-row answer-response">{desc}</div>
      </div>
    );
  }

  onAnswer = (selectedAnswer) => {
    this.setState({
      selectedAnswer,
    });
  };

  submitAnswer = () => {
    if (this.state.selectedAnswer !== -1) {
      this.props.chooseAnswer(this.props.id, this.state.selectedAnswer);
    }
  };

  focusQuestion = () => {
    if (this.questionRef.current) {
      this.questionRef.current.focus();
    }
  };

  renderQuestionHTML() {
    return {
      __html: converter.makeHtml(this.props.description),
    };
  }

  renderCorrectAnswerHTML() {
    const {
      isCorrect,
      attempts,
      explanation,
      correctAnswerText,
    } = this.props.response;
    let description = null,
      explanationText = null;

    if (correctAnswerText) {
      description = (
        <div>
          <h5>
            <FontAwesomeIcon
              icon={this.props.response.isCorrect ? faCheck : faTimes}
            />{' '}
            {isCorrect ? <span>That's right!</span> : <span>Incorrect</span>}
          </h5>
          <p>
            The correct answer is:{' '}
            <strong dangerouslySetInnerHTML={{ __html: correctAnswerText }} />
          </p>
        </div>
      );
    }

    if (explanation) {
      explanationText = <p dangerouslySetInnerHTML={{ __html: explanation }} />;
    }

    let answered;
    if (isCorrect === false) {
      answered = <p>You answered this question incorrectly.</p>;
    } else if (attempts === 1) {
      answered = (
        <p>You answered this question correctly on the first attempt.</p>
      );
    } else {
      answered = <p>You answered this question in {attempts} attempts.</p>;
    }

    return (
      <div className="correct-answer">
        {description}
        {explanationText}
        {answered}
      </div>
    );
  }

  renderAnswerChoices(questionId) {
    const disableQuestions =
      this.state.showFeedback ||
      this.props.checking ||
      this.props.response.isAnswered;
    const { id, response } = this.props;

    let answerChoices = this.props.answerChoices;
    if (!answerChoices.length) {
      answerChoices = [
        {
          id: 1,
          description: 'True',
        },
        {
          id: 0,
          description: 'False',
        },
      ];
    }

    const compareId = response.isAnswered
      ? response.answer?.id || response.answerId
      : this.state.selectedAnswer;

    let choices = answerChoices.map((answer, index) => (
      <MultipleChoiceAnswer
        key={answer.id}
        questionId={id}
        checked={answer.id === compareId}
        disabled={disableQuestions}
        selectAnswer={this.onAnswer}
        correctResponse={response.isCorrect}
        showRightOrWrong={response.isAnswered && answer.id === compareId}
        ref={index === 0 ? this.firstAnswerRef : null}
        {...answer}
      />
    ));

    const showSubmitButton =
      this.state.selectedAnswer !== -1 &&
      !response.isAnswered &&
      !this.state.showFeedback;

    return (
      <div
        className="answer-choices"
        role="group"
        aria-labelledby={questionId}
      >
        {choices}
        {showSubmitButton && (
          <Button
            variant="primary"
            onClick={this.submitAnswer}
            disabled={this.props.checking}
            className="mt-2"
            aria-label="Submit answer"
          >
            Submit Answer
          </Button>
        )}
      </div>
    );
  }

  render() {
    const questionId = `question-${this.props.id}`;
    return (
      <>
        <div className="question p-3 pb-0" ref={this.questionRef} tabIndex="-1">
          <span id={questionId} dangerouslySetInnerHTML={this.renderQuestionHTML()} />
          {this.renderAnswerChoices(questionId)}
        </div>
        <div
          className="feedback m-3"
          aria-live="polite"
          aria-atomic="true"
        >
          {this.props.checking === true ||
          this.state.showFeedback ||
          this.props.response.isAnswered
            ? this.renderFeedback()
            : null}
        </div>
      </>
    );
  }
}

MultipleChoiceQuestion.propTypes = {
  id: PropTypes.number.isRequired,
  description: PropTypes.string,
  answerChoices: PropTypes.array,
  checking: PropTypes.bool,
  chooseAnswer: PropTypes.func.isRequired,
  onAnswerCorrect: PropTypes.func,
  questionNumber: PropTypes.number,
  response: PropTypes.shape({
    attempts: PropTypes.number,
    isAnswered: PropTypes.bool,
    isCorrect: PropTypes.bool,
    answerId: PropTypes.number,
    answerText: PropTypes.string,
    correctAnswerId: PropTypes.number,
    correctAnswerText: PropTypes.string,
    explanation: PropTypes.string,
  }),
};

MultipleChoiceQuestion.defaultProps = {
  id: 0,
  description: '',
  answerChoices: [],
  checking: false,
  questionNumber: undefined,
  response: {
    attempts: 0,
    isAnswered: false,
    isCorrect: false,
    answerId: 0,
    answerText: '',
    correctAnswerId: 0,
    correctAnswerText: '',
    explanation: '',
  },
};

export default MultipleChoiceQuestion;
